export type AgentFileType = 'pdf' | 'unknown';

/** Source for a complete S3 PDF path. */
const S3_FILE_PATH_SOURCE = String.raw`\bs3://\S+\.pdf\b`;

/** AWS S3 presigned PDF URL — query may contain quotes and runs until Expires=<digits>. */
const AWS_PRESIGNED_PDF_URL_SOURCE = String.raw`https?://\S+?\.pdf\?(?:[\s\S]*?&Expires=\d+)`;

/** HTTPS PDF URL with query string (no whitespace in URL). */
const HTTPS_PDF_WITH_QUERY_SOURCE = String.raw`https?://\S+?\.pdf\?(?:(?!\s).)+`;

/** Plain HTTPS PDF URL without query string. */
const HTTPS_PDF_URL_SOURCE = String.raw`https?://\S+?\.pdf(?!\?)`;

/** Global matcher kept for external callers. Do NOT use internally: a shared /g regex carries `lastIndex` state across calls. */
export const S3_FILE_PATH_REGEX = new RegExp(S3_FILE_PATH_SOURCE, 'gi');

/** Stateless matcher for boolean checks (no `lastIndex` to leak between calls). */
const S3_FILE_PATH_TEST = new RegExp(S3_FILE_PATH_SOURCE, 'i');
const AWS_PRESIGNED_PDF_URL_TEST = new RegExp(AWS_PRESIGNED_PDF_URL_SOURCE, 'i');
const HTTPS_PDF_WITH_QUERY_TEST = new RegExp(HTTPS_PDF_WITH_QUERY_SOURCE, 'i');
const HTTPS_PDF_URL_TEST = new RegExp(HTTPS_PDF_URL_SOURCE, 'i');

function createS3FilePathMatcher(): RegExp {
  return new RegExp(S3_FILE_PATH_SOURCE, 'gi');
}

function createAwsPresignedPdfUrlMatcher(): RegExp {
  return new RegExp(AWS_PRESIGNED_PDF_URL_SOURCE, 'gi');
}

function createHttpsPdfWithQueryMatcher(): RegExp {
  return new RegExp(HTTPS_PDF_WITH_QUERY_SOURCE, 'gi');
}

function createHttpsPdfUrlMatcher(): RegExp {
  return new RegExp(HTTPS_PDF_URL_SOURCE, 'gi');
}

const S3_INCOMPLETE_TAIL_REGEX = /\bs3:\/\/\S*$/i;
const PRESIGNED_INCOMPLETE_TAIL_REGEX = /\bhttps?:\/\/\S*$/i;

const DISPLAY_NAME_MAX_LENGTH = 32;

export interface S3FileAttachmentSegment {
  kind: 'fileAttachment';
  /** s3:// path — resolved via presigned URL API when presignedUrl is absent. */
  s3Path: string;
  /** Direct HTTPS presigned URL — used as-is for preview/download. */
  presignedUrl?: string;
  fileName: string;
  displayFileName: string;
  fileType: AgentFileType;
}

export type MarkdownOrFileSegment =
  | { kind: 'markdown'; content: string }
  | S3FileAttachmentSegment;

export function getFileTypeFromPath(path: string): AgentFileType {
  return path.split('.').pop()?.toLowerCase() === 'pdf' ? 'pdf' : 'unknown';
}

export function extractFileNameFromS3Path(s3Path: string): string {
  const segments = s3Path.replace(/^s3:\/\//i, '').split('/');
  const raw = segments[segments.length - 1] ?? s3Path;
  return raw.trim() || s3Path;
}

export function extractFileNameFromPresignedUrl(presignedUrl: string): string {
  const fromDisposition = extractFilenameFromContentDisposition(presignedUrl);
  if (fromDisposition) {
    return fromDisposition;
  }
  try {
    const pathname = new URL(presignedUrl).pathname;
    const raw = pathname.split('/').pop() ?? '';
    const decoded = decodeURIComponent(raw).trim();
    if (decoded) {
      return decoded;
    }
  } catch {
    // fall through
  }
  return presignedUrl;
}

function extractFilenameFromContentDisposition(url: string): string | null {
  const queryIndex = url.indexOf('?');
  if (queryIndex < 0) {
    return null;
  }
  const params = new URLSearchParams(url.slice(queryIndex + 1));
  const utf8Name = params.get('response-content-disposition') ?? params.get('Content-Disposition') ?? '';
  const utf8Match = utf8Name.match(/filename\*=(?:UTF-8''|utf-8'')([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].replace(/"/g, '').trim());
    } catch {
      return utf8Match[1].replace(/"/g, '').trim();
    }
  }
  const plainMatch = utf8Name.match(/filename="([^"]+)"/i) ?? utf8Name.match(/filename=([^;]+)/i);
  if (plainMatch?.[1]) {
    try {
      return decodeURIComponent(plainMatch[1].replace(/"/g, '').trim());
    } catch {
      return plainMatch[1].replace(/"/g, '').trim();
    }
  }
  return null;
}

/** Truncates long filenames for display (e.g. Impact Assessment..pdf). */
export function formatDisplayFileName(fileName: string, maxLength = DISPLAY_NAME_MAX_LENGTH): string {
  if (fileName.length <= maxLength) {
    return fileName;
  }
  const dotIndex = fileName.lastIndexOf('.');
  if (dotIndex <= 0) {
    return `${fileName.slice(0, maxLength - 2)}..`;
  }
  const extension = fileName.slice(dotIndex);
  const baseMax = Math.max(1, maxLength - extension.length - 2);
  const base = fileName.slice(0, dotIndex);
  if (base.length <= baseMax) {
    return fileName;
  }
  return `${base.slice(0, baseMax)}..${extension}`;
}

/**
 * When streaming, holds back a trailing partial s3:// path so it stays plain text
 * until the full path (with extension) is received.
 */
export function detectIncompleteS3Tail(text: string, isStreaming: boolean): string | null {
  if (!isStreaming || !text) {
    return null;
  }
  const match = S3_INCOMPLETE_TAIL_REGEX.exec(text);
  if (!match) {
    return null;
  }
  const tail = match[0];
  if (S3_FILE_PATH_TEST.test(tail)) {
    return null;
  }
  return tail;
}

/** While streaming, hold back trailing https URLs until the stream completes (query params may still arrive). */
export function detectIncompletePresignedUrlTail(text: string, isStreaming: boolean): string | null {
  if (!isStreaming || !text) {
    return null;
  }
  const match = PRESIGNED_INCOMPLETE_TAIL_REGEX.exec(text);
  return match?.[0] ?? null;
}

export function hasCompleteS3FilePaths(markdown: string, isStreaming = false): boolean {
  const { searchable } = stripIncompleteFileAttachmentTails(markdown, isStreaming);
  const normalized = joinLineBrokenPresignedUrls(searchable);
  return (
    S3_FILE_PATH_TEST.test(normalized) ||
    AWS_PRESIGNED_PDF_URL_TEST.test(normalized) ||
    HTTPS_PDF_WITH_QUERY_TEST.test(normalized) ||
    HTTPS_PDF_URL_TEST.test(normalized)
  );
}

function stripIncompleteFileAttachmentTails(
  text: string,
  isStreaming: boolean
): { searchable: string; heldBack: string } {
  const s3Tail = detectIncompleteS3Tail(text, isStreaming);
  if (s3Tail) {
    const index = text.lastIndexOf(s3Tail);
    return {
      searchable: text.slice(0, index),
      heldBack: s3Tail,
    };
  }
  const presignedTail = detectIncompletePresignedUrlTail(text, isStreaming);
  if (presignedTail) {
    const index = text.lastIndexOf(presignedTail);
    return {
      searchable: text.slice(0, index),
      heldBack: presignedTail,
    };
  }
  return { searchable: text, heldBack: '' };
}

/** Rejoin query-string lines split by a newline (e.g. before &AWSAccessKeyId). */
function joinLineBrokenPresignedUrls(text: string): string {
  let result = text;
  let prev = '';
  while (prev !== result) {
    prev = result;
    result = result.replace(/([=%\w./%-])\n(\s*&[A-Za-z0-9_-]+=)/g, '$1$2');
  }
  return result;
}

/**
 * Per-line matchers for orphaned presigned-URL leftovers. Each is anchored to the
 * start of a single line and contains no nested/ambiguous quantifiers, so it runs in
 * linear time and is safe against ReDoS on untrusted markdown content.
 */
const ORPHANED_AWS_PARAM_LINE =
  /^[^\S\n]*&(?:AWSAccessKeyId|Signature|x-amz-security-token|Expires)=/i;
const ORPHANED_FILENAME_DISPOSITION_LINE = /^[^\S\n]*(?:;[^\S\n]*)?filename\*=(?:UTF-8''|utf-8'')/i;
const ORPHANED_PDF_QUOTE_LINE = /^[^\S\n]*"[^"\n]+\.pdf[^"\n]*"/i;

function isOrphanedPresignedFragmentLine(line: string): boolean {
  return (
    ORPHANED_AWS_PARAM_LINE.test(line) ||
    ORPHANED_FILENAME_DISPOSITION_LINE.test(line) ||
    ORPHANED_PDF_QUOTE_LINE.test(line)
  );
}

/**
 * Remove query-string fragments left behind when a presigned URL was partially matched.
 *
 * Implemented as a single linear pass over the lines rather than with multi-line regexes:
 * the previous regex-based version used unbounded repetitions (`[^\n]+`) followed by
 * whitespace-including separators, which is vulnerable to super-linear (polynomial)
 * backtracking and could be abused for denial of service. A run of consecutive orphaned
 * lines collapses to a single blank line, preserving the original output shape.
 */
function stripOrphanedPresignedUrlFragments(content: string): string {
  if (!content.trim()) {
    return content;
  }
  const lines = content.split('\n');
  const result: string[] = [];
  let previousWasOrphan = false;
  for (const line of lines) {
    if (isOrphanedPresignedFragmentLine(line)) {
      if (!previousWasOrphan) {
        result.push('');
      }
      previousWasOrphan = true;
      continue;
    }
    result.push(line);
    previousWasOrphan = false;
  }
  return result.join('\n');
}

interface FileAttachmentMatch {
  index: number;
  value: string;
  presignedUrl?: string;
  s3Path?: string;
}

function overlapsExisting(
  matches: FileAttachmentMatch[],
  index: number,
  length: number
): boolean {
  return matches.some(
    (existing) => index >= existing.index && index < existing.index + existing.value.length
  );
}

function collectFileAttachmentMatches(normalized: string): FileAttachmentMatch[] {
  const matches: FileAttachmentMatch[] = [];

  const s3Matcher = createS3FilePathMatcher();
  let s3Match: RegExpExecArray | null;
  while ((s3Match = s3Matcher.exec(normalized)) !== null) {
    matches.push({ index: s3Match.index, value: s3Match[0], s3Path: s3Match[0] });
  }

  const awsMatcher = createAwsPresignedPdfUrlMatcher();
  let awsMatch: RegExpExecArray | null;
  while ((awsMatch = awsMatcher.exec(normalized)) !== null) {
    if (!overlapsExisting(matches, awsMatch.index, awsMatch[0].length)) {
      matches.push({ index: awsMatch.index, value: awsMatch[0], presignedUrl: awsMatch[0] });
    }
  }

  const httpsQueryMatcher = createHttpsPdfWithQueryMatcher();
  let httpsQueryMatch: RegExpExecArray | null;
  while ((httpsQueryMatch = httpsQueryMatcher.exec(normalized)) !== null) {
    if (!overlapsExisting(matches, httpsQueryMatch.index, httpsQueryMatch[0].length)) {
      matches.push({
        index: httpsQueryMatch.index,
        value: httpsQueryMatch[0],
        presignedUrl: httpsQueryMatch[0],
      });
    }
  }

  const httpsMatcher = createHttpsPdfUrlMatcher();
  let httpsMatch: RegExpExecArray | null;
  while ((httpsMatch = httpsMatcher.exec(normalized)) !== null) {
    if (!overlapsExisting(matches, httpsMatch.index, httpsMatch[0].length)) {
      matches.push({ index: httpsMatch.index, value: httpsMatch[0], presignedUrl: httpsMatch[0] });
    }
  }

  return matches.sort((a, b) => a.index - b.index);
}

function buildFileAttachmentSegment(match: FileAttachmentMatch): S3FileAttachmentSegment {
  if (match.s3Path) {
    const fileName = extractFileNameFromS3Path(match.s3Path);
    return {
      kind: 'fileAttachment',
      s3Path: match.s3Path,
      fileName,
      displayFileName: formatDisplayFileName(fileName),
      fileType: 'pdf',
    };
  }

  const presignedUrl = match.presignedUrl ?? match.value;
  const fileName = extractFileNameFromPresignedUrl(presignedUrl);
  return {
    kind: 'fileAttachment',
    s3Path: '',
    presignedUrl,
    fileName,
    displayFileName: formatDisplayFileName(fileName),
    fileType: 'pdf',
  };
}

function cleanMarkdownSegmentContent(content: string): string {
  return stripOrphanedPresignedUrlFragments(content);
}

export function splitMarkdownByS3Paths(markdown: string, isStreaming = false): readonly MarkdownOrFileSegment[] {
  const { searchable, heldBack } = stripIncompleteFileAttachmentTails(markdown, isStreaming);
  const normalized = joinLineBrokenPresignedUrls(searchable);
  const normalizedHeldBack = joinLineBrokenPresignedUrls(heldBack);
  const segments: MarkdownOrFileSegment[] = [];
  let lastIndex = 0;

  const matches = collectFileAttachmentMatches(normalized);

  for (const match of matches) {
    const before = cleanMarkdownSegmentContent(normalized.slice(lastIndex, match.index));
    if (before.length > 0) {
      segments.push({ kind: 'markdown', content: before });
    }
    segments.push(buildFileAttachmentSegment(match));
    lastIndex = match.index + match.value.length;
  }

  const remainder = cleanMarkdownSegmentContent(normalized.slice(lastIndex));
  const heldBackClean = cleanMarkdownSegmentContent(normalizedHeldBack);
  if (remainder.length > 0 || heldBackClean.length > 0) {
    segments.push({ kind: 'markdown', content: remainder + heldBackClean });
  }

  if (segments.length === 0 && markdown.trim().length > 0) {
    segments.push({ kind: 'markdown', content: markdown });
  }

  return segments;
}
